@if (isProcessing()) {
<app-generation-loader title="Formula" description="Generating the geotechnical formula dashboard"
    [animationData]="generationAnimationData" [bottomImageSvg]="GEOTECHNICAL_ANALYSIS_SVG.FORMULA_LOADER">
</app-generation-loader>
} @else if (isFailed()) {
<div class="flex flex-col h-full w-full">
    <div class="flex justify-between items-center p-6 w-full shrink-0">
        <p class="flex items-center font-semibold text-lg gap-2">
            <span>Formula</span>
            <app-icon [type]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [source]="'svg'" [size]="'default'" />
        </p>
    </div>

    <div class="flex flex-1 items-center justify-center px-6 pb-6">
        <div class="flex flex-col items-center gap-2 text-center">
            <p class="text-lg font-semibold text-gray-800">Formula dashboard failed</p>
            <p class="text-sm text-gray-500">{{
                dashboard()?.error || errorMessage()
                || 'Unable to load formula dashboard' }}</p>
        </div>
    </div>
</div>
} @else {
<div class="flex flex-col w-full h-full overflow-y-auto">
    <div class="flex justify-between items-center p-6 w-full shrink-0">
        <p class="flex items-center font-semibold text-lg gap-2">
            <span>Formula</span>
            <app-icon [type]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [source]="'svg'" [size]="'default'" />
        </p>
        <div class="flex gap-3">
            <app-button [icon]="'pencil'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                severity="contrast" pTooltip="Edit" tooltipPosition="top" (clicked)="analysisExcelDialogVisible = true">
            </app-button>
        </div>
    </div>

    @if (isEmpty()) {
    <div class="flex flex-1 items-center justify-center px-6 pb-6">
        <p class="text-sm text-gray-500">No formula dashboard available yet.</p>
    </div>
    } @else {
    <div class="flex flex-col gap-4 px-6 pb-6">
        <p-accordion [multiple]="true" class="formula-accordion">
            @for (section of sections(); track getSectionId(section)) {
            <p-accordion-panel [value]="getSectionId(section)">
                <p-accordion-header>{{ section.title }}</p-accordion-header>
                <p-accordion-content>
                    <div class="space-y-6">
                        @if (section.type === DASHBOARD_SECTION_TYPE.FORMULA && section.formula) {
                        <ng-container *ngTemplateOutlet="formulaContentTemplate; context: { $implicit: section }">
                        </ng-container>
                        }
                        <ng-container *ngTemplateOutlet="parametersTemplate; context: { $implicit: section }">
                        </ng-container>
                        <ng-container *ngTemplateOutlet="parameterValuesTemplate; context: { $implicit: section }">
                        </ng-container>
                    </div>
                </p-accordion-content>
            </p-accordion-panel>
            }
        </p-accordion>
    </div>
    }
</div>
}

<ng-template #formulaContentTemplate let-section>
    <div class="flex flex-col gap-2">
        <span class="font-semibold text-gray-800">Formula</span>
        <div
            class="border-l-2 border-l-arena-orange-800 pl-4 py-2 overflow-x-auto [&_.katex]:text-base [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden">
            <div [innerHTML]="section.formula | markdownRender" class="katex-content"></div>
        </div>
    </div>
</ng-template>

<ng-template #parametersTemplate let-section>
    <div class="flex flex-col gap-2">
        <span class="font-semibold text-gray-800">Parameters</span>
        <div class="flex flex-col gap-2">
            @for (param of section.parameters; track param._id) {
            <div
                class="border-l-2 border-l-arena-orange-800 pl-4 py-2 [&_.katex]:text-sm [&_.katex-display]:overflow-x-auto">
                <div [innerHTML]="param.label + (param.description ? ' = ' + param.description : '') | markdownRender"
                    class="katex-content">
                </div>
            </div>
            }
        </div>
    </div>
</ng-template>

<ng-template #parameterValuesTemplate let-section>
    <div class="flex flex-col gap-2">
        <span class="font-semibold text-gray-800">Parameter values</span>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            @for (param of section.parameters; track param._id) {
            @let paramValue = getParameterValue(param.nameKey);
            <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between">
                    <label class="text-sm font-medium text-gray-700">{{ param.label }}</label>
                    @if (paramValue?.sourceType) {
                    <div class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer"
                        (click)="openSourceDialog(param.nameKey)">
                        Source
                        <app-icon type="external-link" source="lucide" [size]="'xs'" />
                    </div>
                    }
                </div>
                <div class="flex items-center px-2 py-2 min-h-[2.25rem] border border-gray-200 rounded-lg cursor-pointer"
                    (click)="analysisExcelDialogVisible = true">
                    <span class="text-sm font-medium text-gray-700">{{
                        getParameterDisplayValue(param.nameKey) }}</span>
                </div>
            </div>
            }
        </div>
    </div>
</ng-template>

<p-dialog header="Analysis Excel" [(visible)]="analysisExcelDialogVisible" [modal]="true" [draggable]="false"
    [resizable]="false" appendTo="body" [style]="{ width: '92vw', maxWidth: '96rem' }"
    contentStyleClass="!p-0 overflow-hidden" styleClass="analysis-excel-preview-dialog">
    <ng-template pTemplate="content">
        @let analysisExcelPreview = analysisExcelPreviewFile();
        @let analysisExcelEditor = analysisExcelEditorConfig();
        <div class="h-[78vh] bg-white">
            @if (analysisExcelEditor && analysisExcelPreview) {
            <app-only-office-doc-editor class="block h-full w-full" [contractId]="analysisExcelPreview._id"
                [configData]="analysisExcelEditor" [editorId]="'analysis_excel_' + analysisExcelPreview._id">
            </app-only-office-doc-editor>
            } @else {
            <div class="flex h-full items-center justify-center px-6 text-sm text-gray-500">
                Analysis excel is not available.
            </div>
            }
        </div>
    </ng-template>
</p-dialog>

<p-dialog header="Source" [(visible)]="aiSourceDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
    appendTo="body" [style]="{ width: '600px', maxWidth: '95vw' }">
    <div class="flex flex-col gap-5 pb-2">
        <div class="flex flex-col gap-2">
            <span class="font-semibold text-gray-800">Reason</span>
            <div class="border border-orange-400 rounded-lg px-4 py-3 text-sm text-gray-700 leading-relaxed">
                {{ aiSourceDialogParam?.reasoning }}
            </div>
        </div>
        @if (aiSourceDialogParam?.source?.length) {
        @let total = aiSourceDialogParam!.source!.length;
        <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
                <span class="font-semibold text-gray-800">Source</span>
                <div class="flex items-center gap-1">
                    <button type="button" (click)="navigateSource(-1)" [disabled]="aiSourceIndex === 0"
                        class="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <app-icon type="chevron-left" source="lucide" [size]="'sm'" />
                    </button>
                    <button type="button" (click)="navigateSource(1)" [disabled]="aiSourceIndex === total - 1"
                        class="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <app-icon type="chevron-right" source="lucide" [size]="'sm'" />
                    </button>
                </div>
            </div>
            <div class="border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                <p class="font-semibold text-gray-500 mb-2">Source {{ aiSourceIndex + 1 }}</p>
                <p>{{ aiSourceDialogParam!.source![aiSourceIndex] }}</p>
            </div>
        </div>
        }
    </div>
</p-dialog>